/**
 * How many bytes a media element actually pulled over the network.
 *
 * ── WHY THIS IS NOT OBVIOUS ──────────────────────────────────────────────────
 * Video is served straight from the CDN (`R2_PUBLIC_URL`), so the API server is
 * never in the transfer path and cannot count a single byte of it. The only
 * party that knows the true figure is the browser, and it will tell us —
 * `PerformanceResourceTiming.transferSize` is the exact number of bytes that
 * crossed the wire for a resource, and it is **0 when the response came out of
 * the HTTP cache**. That last property is the one that matters: a student
 * re-opening a lecture they watched yesterday costs nothing, and any method
 * based on file size or watch time counts it again.
 *
 * ── THE CROSS-ORIGIN CATCH ───────────────────────────────────────────────────
 * For a cross-origin resource — and `storage.lmscampus.com` is cross-origin to
 * every LMS host — the size fields are **redacted to 0** unless the response
 * carries `Timing-Allow-Origin`. So three states have to be told apart, and two
 * of them look identical at a glance:
 *
 *   transferSize > 0                        → real bytes, exact.
 *   transferSize === 0, decodedBodySize > 0 → served from cache. Really zero.
 *   transferSize === 0, decodedBodySize = 0 → REDACTED. We know nothing.
 *
 * Reading the third case as "zero bytes" would silently report every institute
 * as using no bandwidth at all, which is worse than an over-count because it
 * looks plausible. It reports `unknown` instead, and the server falls back to
 * deriving the figure from watch coverage.
 *
 * Until `Timing-Allow-Origin` is set on the storage domain, every media read
 * here is the third case. Setting it upgrades the platform's numbers from
 * derived to exact with no deploy.
 *
 * ── WHY AN OBSERVER AND NOT getEntriesByType ─────────────────────────────────
 * A media element issues one range request after another, so a 40-minute
 * lecture can produce hundreds of entries. The resource-timing buffer holds 250
 * and then drops the rest **silently**. `PerformanceObserver` receives each
 * entry as it happens and is not subject to that cap. It is also the only way
 * to avoid `performance.clearResourceTimings()`, which takes no argument and
 * would wipe every other consumer's entries along with ours.
 */

/**
 * Start accumulating transfer sizes. One meter per player.
 *
 *   const meter = createMediaBytesMeter();
 *   meter.watch(video.currentSrc);   // whenever the source changes
 *   const { bytes, source } = meter.takeDelta();
 *   meter.stop();                    // on unmount
 */
export function createMediaBytesMeter() {
  let url = null;
  let transferred = 0;   // bytes seen for `url` since the last takeDelta()
  let decoded = 0;       // body bytes for the same, to tell cache from redaction
  let matched = 0;
  let observer = null;

  const consume = (entries) => {
    for (const e of entries) {
      if (!url || e.name !== url) continue;
      matched += 1;
      transferred += Number(e.transferSize) || 0;
      decoded += Number(e.decodedBodySize) || 0;
    }
  };

  if (typeof PerformanceObserver === 'function') {
    try {
      observer = new PerformanceObserver((list) => consume(list.getEntries()));
      // `buffered` replays what already happened — the first range requests fire
      // before React has mounted the effect that creates this meter.
      observer.observe({ type: 'resource', buffered: true });
    } catch {
      observer = null; // unsupported options; fall through to 'unknown'
    }
  }

  return {
    /** Point the meter at a source. Switching video resets the pending delta. */
    watch(nextUrl) {
      if (nextUrl === url) return;
      url = nextUrl || null;
      transferred = 0;
      decoded = 0;
      matched = 0;
    },

    /**
     * Bytes transferred since the previous call, and how much to trust it.
     * Reading resets the counter, so every byte is reported exactly once.
     */
    takeDelta() {
      if (!observer || !url || !matched) return { bytes: 0, source: 'unknown' };

      const bytes = transferred;
      const body = decoded;
      transferred = 0;
      decoded = 0;
      matched = 0;

      if (bytes > 0) return { bytes, source: 'timing' };
      // Nothing on the wire but a body was decoded — the cache served it, and
      // zero is the true answer rather than a missing one.
      if (body > 0) return { bytes: 0, source: 'timing' };
      // Both zero: cross-origin redaction. Say so; do not claim zero.
      return { bytes: 0, source: 'unknown' };
    },

    /**
     * Hand a previously-taken delta back, after the report carrying it failed.
     *
     * Without this a dropped request silently loses those bytes for good: the
     * observer has already moved past those entries and nothing will replay
     * them. Watch time does not need the same treatment — it is absolute, so
     * the next flush resends it.
     */
    credit(bytes) {
      const n = Number(bytes) || 0;
      if (n > 0) {
        transferred += n;
        matched += 1; // keep takeDelta from reading the restored bytes as "no data"
      }
    },

    stop() {
      try { observer?.disconnect(); } catch { /* best effort */ }
      observer = null;
    },
  };
}

export default createMediaBytesMeter;
